import { computed } from "vue";

// Lets a phone user opt into the desktop layout (and back). The preference is
// a cookie so the server renders the matching viewport on first paint.
//
// "desktop" on a phone works by widening the layout viewport to a desktop
// width, the same mechanism as Safari's "Request Desktop Website": every
// width-based style rule and the useIsPhone flag then agree it is a desktop,
// and the user pinch-zooms as on any desktop site. Nothing is duplicated per
// layout.
export type LayoutPreference = "auto" | "desktop" | "mobile";

export const DESKTOP_LAYOUT_VIEWPORT = "width=1280, viewport-fit=cover";
export const DEFAULT_VIEWPORT = "width=device-width, initial-scale=1, viewport-fit=cover";

const MOBILE_UA = /iPhone|iPod|Android.*Mobile|Windows Phone|Mobile Safari/i;
const TABLET_UA = /iPad|Android(?!.*Mobile)|Tablet/i;

export function isMobileUserAgent(userAgent: string | null | undefined) {
	const ua = String(userAgent || "");
	if (!ua) return false;
	if (MOBILE_UA.test(ua)) return true;
	// iPadOS reports itself as a Mac; touch points give it away.
	if (TABLET_UA.test(ua)) return true;
	return false;
}

export function useLayoutPreference() {
	const preference = useCookie<LayoutPreference>("leadx_layout", {
		default: () => "auto",
		maxAge: 60 * 60 * 24 * 365,
		sameSite: "lax",
	});

	// Resolved once per request on the server and carried to the client so the
	// switches render the same way on both sides.
	const isMobileDevice = useState<boolean>("layout-is-mobile-device", () => {
		if (import.meta.server) {
			const headers = useRequestHeaders(["user-agent"]);
			return isMobileUserAgent(headers["user-agent"]);
		}
		return isMobileUserAgent(typeof navigator === "undefined" ? "" : navigator.userAgent);
	});

	// A phone showing the desktop layout.
	const forcedDesktop = computed(() => isMobileDevice.value && preference.value === "desktop");

	const viewportContent = computed(() => (forcedDesktop.value ? DESKTOP_LAYOUT_VIEWPORT : DEFAULT_VIEWPORT));

	function reloadForLayout() {
		if (!import.meta.client) return;
		// A reload re-evaluates every media query against the new viewport in
		// one step; toggling the meta tag live leaves Safari half-switched.
		window.location.reload();
	}

	function useDesktopLayout() {
		preference.value = "desktop";
		reloadForLayout();
	}

	function useMobileLayout() {
		preference.value = "mobile";
		reloadForLayout();
	}

	return {
		preference,
		isMobileDevice,
		forcedDesktop,
		viewportContent,
		useDesktopLayout,
		useMobileLayout,
	};
}
